import { useEffect } from 'react'
import Swal from 'sweetalert2'
import Stats from '@/widgets/LeadsStats'

import useLeadsData from '@/hooks/useLeadsData'
import { useStatusFilter } from './filters/StatusFilter'
import { useDateRangeFilter } from './filters/DateRangeFilter'
import { useCampaignFilter } from './filters/CampaignFilter'

import { useLeadSelection } from './hooks/useLeadSelection'
import { useLeadActions } from './hooks/useLeadActions'

import SearchActionsBar from '@/widgets/SearchActionsBar'
import LeadsTable from './components/LeadsTable'
import LoadMoreSection from '@/widgets/LoadMoreSection'

export default function Leads() {
	const {
		data: leadsData,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData: loadLeadsData,
		handleLoadMore,
		setError,
		setSorting: setLeadsSorting,
		handleSearch,
		sortColumn,
		sortDirection,
		searchQuery,
		updateFilters,
	} = useLeadsData()

	const { selectedLeads, handleSelectAll, handleSelectLead, clearSelection, getSelectionState } = useLeadSelection()
	const { isDeleting, handleDeleteSelected } = useLeadActions(loadLeadsData)

	const { selectedStatus, handleStatusFilter } = useStatusFilter(updateFilters)
	const { selectedCampaign, handleCampaignFilter } = useCampaignFilter(updateFilters)

	const { fromDate, toDate, handleDateRangeFilter, getDateRangeDisplay } = useDateRangeFilter(updateFilters)

	useEffect(() => {
		if (error) {
			Swal.fire({ title: error, icon: 'error' }).then(() => setError())
		}
	}, [error, setError])

	useEffect(() => {
		loadLeadsData()
	}, [loadLeadsData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setLeadsSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		handleSearch(e.target.value)
	}

	const { isAllSelected, isIndeterminate } = getSelectionState(leadsData)

	const onDeleteSelected = () => {
		handleDeleteSelected(selectedLeads, clearSelection)
	}

	const onSelectAll = checked => {
		handleSelectAll(checked, leadsData)
	}

	const buttons = [
		{
			type: 'delete',
			label: 'Delete',
			count: selectedLeads.size,
			onClick: onDeleteSelected,
			loading: isDeleting,
		},
		{
			type: 'date',
			label: 'Status',
			active: !!selectedStatus,
			onClick: handleStatusFilter,
		},
		{
			type: 'date',
			label: 'Campaign',
			active: !!selectedCampaign,
			onClick: handleCampaignFilter,
			title: selectedCampaign ? `Campaign: ${selectedCampaign}` : 'Filter by campaign',
		},
		{
			type: 'date',
			label: 'Lead created',
			active: !!(fromDate || toDate),
			onClick: handleDateRangeFilter,
			title: getDateRangeDisplay() || 'Filter by date range',
		},
		{
			type: 'add',
			label: 'Add new',
			to: '/leads/add',
		},
	]

	return (
		<>
			<Stats />

			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder="Search leads by name, email, or phone..."
				buttons={buttons}
			/>

			<LeadsTable
				leadsData={leadsData}
				loading={loading}
				selectedLeads={selectedLeads}
				onSelectAll={onSelectAll}
				onSelectLead={handleSelectLead}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={leadsData.length}
				totalCount={totalCount}
			/>
		</>
	)
}
